import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type PropsWithChildren,
} from 'react';

/** Per-browser memory of whether the docked assistant panel was open. */
export const AGENT_PANEL_OPEN_STORAGE_KEY = 'nami.panel.open';

function readStoredOpen(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(AGENT_PANEL_OPEN_STORAGE_KEY) === '1';
  } catch {
    // Private windows and blocked storage throw; closed is the safe default.
    return false;
  }
}

function writeStoredOpen(isOpen: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      AGENT_PANEL_OPEN_STORAGE_KEY,
      isOpen ? '1' : '0',
    );
  } catch {
    // Persistence is a convenience, never a requirement.
  }
}

export interface AgentPanelContextData {
  isOpen: boolean;
  /** Whether the panel was opened from a modal (affects layout shift behavior) */
  isOpenedFromModal: boolean;
  open: (fromModal?: boolean) => void;
  close: () => void;
  toggle: (fromModal?: boolean) => void;
  /**
   * The last in-app path that could show the docked panel (never the
   * full-page chat), so "Dock to side" knows where to bring the conversation.
   */
  lastSurfacePath: string | null;
  rememberSurfacePath: (path: string) => void;
  /**
   * Set by the full-page chat's "Dock to side". The panel opens itself once a
   * page with a surface renders, instead of fighting the close-on-chat rule.
   */
  dockRequested: boolean;
  requestDock: () => void;
  consumeDockRequest: () => void;
}

const AgentPanelContext = createContext<AgentPanelContextData | undefined>(
  undefined,
);

export function AgentPanelProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState(() => ({
    isOpen: readStoredOpen(),
    isOpenedFromModal: false,
  }));
  const [lastSurfacePath, setLastSurfacePath] = useState<string | null>(null);
  const [dockRequested, setDockRequested] = useState(false);

  // The docked panel travels with the user across pages and reloads.
  useEffect(() => {
    writeStoredOpen(state.isOpen);
  }, [state.isOpen]);

  const open = useCallback((fromModal = false) => {
    setState({ isOpen: true, isOpenedFromModal: fromModal });
  }, []);

  const close = useCallback(() => {
    setState({ isOpen: false, isOpenedFromModal: false });
  }, []);

  const toggle = useCallback((fromModal = false) => {
    setState((prev) => ({
      isOpen: !prev.isOpen,
      isOpenedFromModal: !prev.isOpen ? fromModal : false,
    }));
  }, []);

  // Plain functions: the React Compiler memoizes them (no manual useCallback).
  const rememberSurfacePath = (path: string) => {
    setLastSurfacePath((prev) => (prev === path ? prev : path));
  };
  const requestDock = () => setDockRequested(true);
  const consumeDockRequest = () => setDockRequested(false);

  return (
    <AgentPanelContext.Provider
      value={{
        isOpen: state.isOpen,
        isOpenedFromModal: state.isOpenedFromModal,
        open,
        close,
        toggle,
        lastSurfacePath,
        rememberSurfacePath,
        dockRequested,
        requestDock,
        consumeDockRequest,
      }}
    >
      {children}
    </AgentPanelContext.Provider>
  );
}

export function useAgentPanel() {
  const context = useContext(AgentPanelContext);
  if (context === undefined) {
    throw new Error('useAgentPanel must be used within an AgentPanelProvider');
  }
  return context;
}
